import { useState } from 'react';
import { BRANCHES, GRADE_LEVELS } from '@/mocks/mockData';
import type { Teacher } from '@/mocks/mockData';
import { supabase } from '@/lib/supabase';

interface Props {
  onClose: () => void;
  onAdd: (teacher: Teacher) => void;
}

export default function AddTeacherModal({ onClose, onAdd }: Props) {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', password: '',
    branches: [] as string[],
    gradeLevels: [] as string[], canOnline: true, canFaceToFace: false,
    availability: '', defaultRate: 350, commissionRate: 50, status: 'Aktif' as 'Aktif' | 'Pasif', note: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');

  const toggleBranch = (b: string) => setForm((p) => ({ ...p, branches: p.branches.includes(b) ? p.branches.filter((x) => x !== b) : [...p.branches, b] }));
  const toggleGrade = (g: string) => setForm((p) => ({ ...p, gradeLevels: p.gradeLevels.includes(g) ? p.gradeLevels.filter((x) => x !== g) : [...p.gradeLevels, g] }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setGeneratedPassword('');

    if (!form.name || !form.email) {
      setError('Ad soyad ve e-posta adresi zorunludur.');
      return;
    }

    setSaving(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        setError('Oturumunuz geçersiz. Lütfen tekrar giriş yapın.');
        setSaving(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-teacher-account`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            ad_soyad: form.name,
            email: form.email,
            telefon: form.phone || null,
            branslar: form.branches,
            sinif_seviyeleri: form.gradeLevels,
            online_ders: form.canOnline,
            yuz_yuze_ders: form.canFaceToFace,
            musaitlik_notu: form.availability || null,
            varsayilan_ders_ucreti: form.defaultRate,
            hakedis_orani: form.commissionRate,
            aciklama: form.note || null,
            baslangic_sifre: form.password || undefined,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || result.error) {
        setError(result.error || 'Öğretmen kaydedilemedi. Lütfen tekrar deneyin.');
        setSaving(false);
        return;
      }

      setGeneratedPassword(result.gecici_sifre || '');

      const teacher: Teacher = {
        id: result.ogretmen_id || '',
        name: form.name,
        phone: form.phone,
        email: form.email,
        branches: form.branches,
        gradeLevels: form.gradeLevels,
        canOnline: form.canOnline,
        canFaceToFace: form.canFaceToFace,
        availability: form.availability,
        defaultRate: form.defaultRate,
        commissionRate: form.commissionRate,
        activeStudents: 0,
        lessonsThisMonth: 0,
        earningsThisMonth: 0,
        pendingEarnings: 0,
        status: form.status,
        joinDate: new Date().toISOString().split('T')[0],
        note: form.note,
      };

      onAdd(teacher);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Beklenmeyen bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">Yeni Öğretmen Ekle</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
              <i className="ri-error-warning-line mr-1" />{error}
            </div>
          )}

          {generatedPassword && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 space-y-1">
              <div className="flex items-center gap-2 text-green-700 text-sm font-semibold">
                <i className="ri-checkbox-circle-line" />
                Hesap başarıyla oluşturuldu!
              </div>
              <p className="text-green-600 text-xs">
                Öğretmene iletilmesi gereken bilgiler:
              </p>
              <div className="bg-white rounded-lg border border-green-100 px-3 py-2 mt-1">
                <p className="text-xs text-slate-500">E-posta:</p>
                <p className="text-sm font-medium text-slate-800">{form.email}</p>
                <p className="text-xs text-slate-500 mt-1">Geçici Şifre:</p>
                <p className="text-sm font-mono font-medium text-slate-800">{generatedPassword}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const text = `E-posta: ${form.email}\nŞifre: ${generatedPassword}`;
                  navigator.clipboard.writeText(text);
                }}
                className="mt-2 w-full py-2 rounded-lg border border-green-300 bg-white text-green-700 text-sm font-medium hover:bg-green-100 transition-colors cursor-pointer"
              >
                <i className="ri-clipboard-line mr-1" /> Kopyala
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Ad Soyad *</label>
              <input required value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Telefon</label>
              <input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">E-posta *</label>
              <input type="email" required value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Başlangıç Şifresi <span className="text-slate-400 font-normal">(boş bırakılırsa otomatik)</span></label>
              <input type="text" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder="Örn: Ogretmen2026!" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Varsayılan Ders Ücreti (₺)</label>
              <input type="number" value={form.defaultRate} onChange={(e) => setForm((p) => ({ ...p, defaultRate: Number(e.target.value) }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Hak Ediş Oranı (%)</label>
              <input type="number" min={0} max={100} value={form.commissionRate} onChange={(e) => setForm((p) => ({ ...p, commissionRate: Number(e.target.value) }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
              <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as 'Aktif' | 'Pasif' }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                <option>Aktif</option>
                <option>Pasif</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Ders Tipi</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.canOnline} onChange={(e) => setForm((p) => ({ ...p, canOnline: e.target.checked }))} className="rounded" />
                <span className="text-sm text-slate-700">Online ders verebilir</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.canFaceToFace} onChange={(e) => setForm((p) => ({ ...p, canFaceToFace: e.target.checked }))} className="rounded" />
                <span className="text-sm text-slate-700">Yüz yüze ders verebilir</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Branşlar</label>
            <div className="flex flex-wrap gap-2">
              {BRANCHES.map((b) => (
                <button key={b} type="button" onClick={() => toggleBranch(b)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${form.branches.includes(b) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1e3a5f]/40'}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">Sınıf Seviyeleri</label>
            <div className="flex flex-wrap gap-2">
              {GRADE_LEVELS.map((g) => (
                <button key={g} type="button" onClick={() => toggleGrade(g)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${form.gradeLevels.includes(g) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400'}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Müsaitlik Notu</label>
            <input value={form.availability} onChange={(e) => setForm((p) => ({ ...p, availability: e.target.value }))} placeholder="örn: Haftaiçi 14:00-22:00" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Açıklama Notu</label>
            <textarea value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} rows={2} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-60 cursor-pointer">
              {saving ? 'Oluşturuluyor...' : generatedPassword ? 'Tamam' : 'Hesap Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}